import { useMemo } from 'react'

import { nutrimaticLabels } from '@/nutrimatic'
import {
  isSwapEnabledForPair,
  isSwapEnabledForToken,
} from '../../../../config/marketPolicy'
import { Field } from '../../../../domain/trading'
import type { SwapControllerResult } from '../../../../features/swap/useSwapController'
import { useAppWeb3 } from '../../../../hooks'
import { Token } from '../../../../sdk'
import type { NutrimaticMobileNoticeVariant } from './NutrimaticMobileNotice'
import { wrappedCurrency } from '../../../../utils/wrappedCurrency'

export type SwapMobileNotice = {
  variant: NutrimaticMobileNoticeVariant
  title: string
  description?: string
}

export function useSwapMobileNotice(controller: SwapControllerResult): SwapMobileNotice | null {
  const { chainId } = useAppWeb3()
  const { currencies } = controller

  return useMemo(() => {
    const input = wrappedCurrency(currencies[Field.INPUT], chainId)
    const output = wrappedCurrency(currencies[Field.OUTPUT], chainId)

    if (!chainId || !input || !output) return null

    if (!isSwapEnabledForPair(chainId, input, output)) {
      return {
        variant: 'blocked',
        title: nutrimaticLabels.mobileSimple.swapBlockedTitle,
        description: nutrimaticLabels.mobileSimple.swapBlockedHint,
      }
    }

    const disabledToken =
      !isSwapEnabledForToken(chainId, input)
        ? input
        : !isSwapEnabledForToken(chainId, output)
          ? output
          : null

    if (disabledToken instanceof Token) {
      return {
        variant: 'blocked',
        title: nutrimaticLabels.mobileSimple.swapBlockedTitle,
        description: nutrimaticLabels.mobileSimple.swapTokenBlockedHint(disabledToken.symbol ?? ''),
      }
    }

    return null
  }, [chainId, currencies])
}
